import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type {
  BatchBookingResponse,
  BookingResponse,
} from "@bw/shared/types/generated";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import { Page } from "../../../common/components/Page";
import { InviteParticipantDialog } from "../components/InviteParticipantDialog";
import { CancelInvitationControl } from "../components/CancelInvitationControl";
import { useBookingInvitations } from "../hooks/useBookingInvitations";

/**
 * Post-checkout confirmation (slice 3b). Shows the booked slots, the
 * recurring_group_id, and the SERVER-charged total_charged + currency straight
 * from BatchBookingResponse — never a re-quote or a guessed number. The cart is
 * already cleared by the review page before this renders.
 *
 * Each slot row shows the court name carried by the booking response, with no
 * second court-list request and no guessed label.
 *
 * Rendered in-place by ReviewPage on success (the cart store lives one level up,
 * and re-rendering here keeps the just-returned response without a refetch).
 */

/** Venue-local "Tue, Jun 10 · 9:00 AM" for a booked slot, best-effort. */
function whenLabel(startTime: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(startTime));
  } catch {
    return startTime;
  }
}

export function ConfirmationPage({
  result,
  facilityId,
}: {
  result: BatchBookingResponse;
  facilityId: string;
}) {
  const navigate = useNavigate();
  const bookings = result.bookings ?? [];
  // The reservation just booked has no roster/invites yet, so this is the
  // booking-flow front door to invite the people you're playing with. The dialog
  // is scoped to the FIRST booking; useCreateBookingInvitation fans the invite
  // across the whole recurring group via the batch endpoint.
  const [showAdd, setShowAdd] = useState(false);
  const firstBooking = bookings[0];
  const courtCount = new Set(bookings.map((booking) => booking.court_id)).size;

  // Fetch pending invitations for the first booking (which represents the whole
  // recurring group — the batch invite fans across all slots). Enabled only when
  // a firstBooking is known.
  const invitationsQ = useBookingInvitations(
    facilityId,
    firstBooking?.booking_id,
    Boolean(firstBooking),
  );
  const invited = (invitationsQ.data ?? []).filter(
    (inv) => inv.status === "pending",
  );

  // The group size is SEALED at payment (slice ② allocation): show the PAID
  // count read-only, sourced from the booking's player_count — never a stepper.
  // Changing the count after the charge is an explicit out-of-scope follow-up.
  // Singular/plural so a solo booking reads "1 player", never "1 players".
  function playerCountLabel(count: number): string {
    return `${count} ${count === 1 ? "player" : "players"}`;
  }

  return (
    <Page>
      <Paper
        data-testid="confirmation"
        variant="outlined"
        sx={{ overflow: "hidden" }}
      >
        <Stack spacing={1} sx={{ p: { xs: 2, sm: 3 } }}>
          <CheckCircleRounded
            color="success"
            aria-hidden="true"
            sx={{ fontSize: 42 }}
          />
          <Typography component="h1" variant="h4">
            You&apos;re all set — booking confirmed
          </Typography>
          <Typography color="text.secondary">
            {courtCount} {courtCount === 1 ? "court" : "courts"} reserved
            {firstBooking ? (
              <>
                {" · "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {playerCountLabel(firstBooking.player_count ?? 1)}
                </Box>
              </>
            ) : null}
            .
          </Typography>
        </Stack>
        <Divider />

        <List disablePadding>
          {bookings.map((b: BookingResponse) => (
            <ListItem
              key={b.booking_id}
              divider
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1.5,
                gap: 2,
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Typography sx={{ minWidth: 0, flex: 1 }}>
                {b.court_name?.trim() ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 800 }}>
                      {b.court_name.trim()}
                    </Box>
                    {" · "}
                  </>
                ) : null}
                {whenLabel(b.start_time)}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {formatMoney(b.amount_charged, b.currency ?? result.currency)}
                </Typography>
                {/* Per-booking deep link into the detail hub (roster + check-in
                    QR + host add/remove) — distinct from "View my bookings"
                    below, which goes to the LIST. */}
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={() =>
                    navigate(`/book/${facilityId}/bookings/${b.booking_id}`)
                  }
                >
                  View booking
                </Button>
              </Stack>
            </ListItem>
          ))}
        </List>

        {firstBooking ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              alignItems: { sm: "center" },
            }}
          >
            <Typography color="text.secondary" sx={{ flex: 1 }}>
              Playing with others? Invite them to all your courts.
            </Typography>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowAdd(true)}
            >
              Add players
            </Button>
          </Stack>
        ) : null}

        {invited.length > 0 && firstBooking ? (
          <Box
            component="section"
            aria-labelledby="confirmation-invited-title"
            sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}
          >
            <Typography
              id="confirmation-invited-title"
              component="h2"
              variant="subtitle1"
              sx={{ mb: 0.5, fontWeight: 800 }}
            >
              Invited players
            </Typography>
            <List disablePadding aria-label="Invited players">
              {invited.map((inv) => (
                <ListItem key={inv.invitation_id} disableGutters divider>
                  <MaterialPlayerCard
                    userId={inv.invitee_id}
                    displayName={inv.invitee_display_name?.trim() || "—"}
                    actions={
                      <>
                        <Chip label="Invited" size="small" variant="outlined" />
                        {/* Host-only cancel — the host is the only viewer who
                            reaches ConfirmationPage. firstBooking is the group
                            anchor (the batch invite fans across all slots). */}
                        <CancelInvitationControl
                          facilityId={facilityId}
                          bookingId={firstBooking.booking_id}
                          invitation={inv}
                        />
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}

        <Divider />
        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Typography color="text.secondary">Total charged</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {formatMoney(result.total_charged, result.currency)}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={() => navigate(`/book/${facilityId}/bookings`)}
          >
            View my bookings
          </Button>
          <Button
            type="button"
            variant="text"
            onClick={() => navigate(`/book/${facilityId}`)}
          >
            Book more courts
          </Button>
        </Stack>

        {showAdd && firstBooking ? (
          <InviteParticipantDialog
            facilityId={facilityId}
            bookingId={firstBooking.booking_id}
            participants={[]}
            pendingInviteeIds={[]}
            onClose={() => setShowAdd(false)}
          />
        ) : null}
      </Paper>
    </Page>
  );
}
